import React from "react";
import moment from "moment";
import { PoolPrintableReportLayout } from "../../../../../components/Common";
import {
    calculateSwimBlockMeters,
    calculateSwimSessionMeters,
    calculateSwimSetMeters,
    getSwimIntensityDistribution,
} from "../../domain/swimming/calculations";

const textOrDash = (value) => value || "";

const PoolDeckPrintableWorkout = ({ workout, templateTitle, templateDescription, tenantName, tenantLogo }) => {
    if (!workout) return null;

    const blocks = workout.blocks || [];
    const allSets = blocks.flatMap(block => block.sets || []);
    const totalMeters = calculateSwimSessionMeters(workout);
    const distribution = getSwimIntensityDistribution(allSets);

    // Build the details list for the printable layout
    const details = [];
    if (templateDescription) {
        details.push({
            content: <><strong>Objetivos / Instruções:</strong> {templateDescription}</>
        });
    }
    if (totalMeters > 0 || blocks.length > 0 || allSets.length > 0) {
        details.push({
            content: (
                <>
                    <strong>Resumo Técnico:</strong> {
                        [
                            totalMeters > 0 && `Volume Total: ${totalMeters.toLocaleString("pt-BR")} m`,
                            blocks.length > 0 && `Blocos: ${blocks.length}`,
                            allSets.length > 0 && `Séries/Sets: ${allSets.length}`,
                            `Data: ${moment().format("DD/MM/YYYY")}`
                        ].filter(Boolean).join(" | ")
                    }
                </>
            )
        });
    }
    if (distribution.length > 0) {
        details.push({
            content: (
                <>
                    <strong>Distribuição de Zonas:</strong> {
                        distribution.map(item => `${item.zone}: ${item.meters.toLocaleString("pt-BR")}m (${item.percent}%)`).join(" | ")
                    }
                </>
            )
        });
    }

    return (
        <PoolPrintableReportLayout
            tenantName={tenantName}
            tenantLogo={tenantLogo}
            title={templateTitle || "Treino de Natação"}
            meta={<><strong>Sessão:</strong> {workout.name || "Sessão A"}</>}
            details={details}
            footerNote="NeoApp • modo piscina"
        >
            {/* BLOCOS DE TREINO DA PISCINA */}
            <div className="pool-print-blocks">
                {blocks.map((block, blockIndex) => {
                    const blockMeters = calculateSwimBlockMeters(block);
                    return (
                        <section key={block.id || blockIndex} className="pool-print-block" style={{ marginBottom: "25px", pageBreakInside: "avoid" }}>
                            <div className="pool-print-block-title" style={{ 
                                display: "flex", 
                                justifyContent: "space-between", 
                                alignItems: "center", 
                                paddingBottom: "6px", 
                                borderBottom: "2px solid #2c3e50",
                                marginBottom: "8px" 
                            }}>
                                <strong style={{ fontSize: "15px", fontWeight: "800", color: "#2c3e50", textTransform: "uppercase" }}>
                                    {block.name || `Bloco ${blockIndex + 1}`}
                                </strong>
                                <span style={{ fontSize: "12px", fontWeight: "bold", backgroundColor: "#e8f5e9", color: "#27ae60", padding: "2px 8px", borderRadius: "4px" }}>
                                    {blockMeters.toLocaleString("pt-BR")} m
                                </span>
                            </div>
                            {block.description && (
                                <p style={{ margin: "0 0 10px 0", fontSize: "12px", fontStyle: "italic", color: "#666" }}>
                                    {block.description}
                                </p>
                            )}

                            <table className="pool-print-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "10px" }}>
                                <thead>
                                    <tr style={{ backgroundColor: "#f8f9fa", color: "#333", textAlign: "left", borderBottom: "2px solid #ddd" }}>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Série</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Tipo</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Estilo</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Zona</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Saída</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Material</th>
                                        <th style={{ padding: "10px 12px", fontWeight: "600", fontSize: "13px" }}>Foco / Obs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(block.sets || []).map((set, setIndex) => {
                                        const meters = calculateSwimSetMeters(set);
                                        const notes = [set.technicalFocus, set.description].filter(Boolean).join(" - ");
                                        return (
                                            <tr key={set.id || setIndex} style={{ borderBottom: "1px solid #eee", backgroundColor: setIndex % 2 === 0 ? "#fff" : "#fafafa" }}>
                                                <td className="pool-print-series" style={{ padding: "10px 12px", fontWeight: "600" }}>
                                                    {set.reps || 1}x{set.distance || "-"}m
                                                    <span style={{ display: "block", fontSize: "10px", color: "#777", fontWeight: "normal", marginTop: "2px" }}>
                                                        Subtotal: {meters.toLocaleString("pt-BR")}m
                                                    </span>
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>{textOrDash(set.setType)}</td>
                                                <td style={{ padding: "10px 12px" }}>{textOrDash(set.style)}</td>
                                                <td style={{ padding: "10px 12px", color: "#e67e22", fontWeight: "600" }}>{textOrDash(set.intensity)}</td>
                                                <td style={{ padding: "10px 12px" }}>{textOrDash(set.interval)}</td>
                                                <td style={{ padding: "10px 12px", color: "#2980b9" }}>{textOrDash(set.equipment)}</td>
                                                <td style={{ padding: "10px 12px", fontSize: "12px", color: "#555" }}>{textOrDash(notes)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </section>
                    );
                })}
            </div>
        </PoolPrintableReportLayout>
    );
};

export default PoolDeckPrintableWorkout;
